import React, { useState, useEffect } from "react";
import {
  Table,
  Card,
  Row,
  Col,
  Statistic,
  Input,
  Select,
  Button,
  Tag,
  Space,
  Avatar,
  Tooltip,
  message,
  Modal,
  Typography,
  Descriptions,
  Badge,
  Form, // Added Form
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  BankOutlined,
  UserOutlined,
  TeamOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { organizationService } from "../services/organizationService";
import OrganizationDetail from "./OrganizationDetail";

const { Title, Text } = Typography;
const { Option } = Select;
const { confirm } = Modal;

const Organizations = () => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false); // New state for create modal
  const [createForm] = Form.useForm(); // Form instance for create organization modal
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    search: "",
    plan: "all",
    status: "all",
  });
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [showDetailView, setShowDetailView] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState(null);

  // Fetch organizations
  const fetchOrganizations = async (params = {}) => {
    setLoading(true);
    try {
      const queryParams = {
        page: pagination.current,
        limit: pagination.pageSize,
        ...filters,
        ...params,
      };

      // Remove 'all' values
      Object.keys(queryParams).forEach((key) => {
        if (queryParams[key] === "all") {
          delete queryParams[key];
        }
      });

      const response = await organizationService.getOrganizations(queryParams);

      if (response.success) {
        setOrganizations(response.data.organizations);
        setPagination((prev) => ({
          ...prev,
          total: response.data.pagination.totalRecords,
          current: response.data.pagination.current,
        }));
      }
    } catch (error) {
      message.error(error.message || "Failed to fetch organizations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, [pagination.current, pagination.pageSize]);

  // Handle search
  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchOrganizations({ page: 1 });
  };

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Handle table change (pagination, sorting, filtering)
  const handleTableChange = (paginationConfig) => {
    setPagination((prev) => ({
      ...prev,
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    }));
  };

  // View organization details
  const viewOrganization = (org) => {
    setSelectedOrg(org);
    setDetailModalVisible(true);
  };

  // Handle row click to show detail view
  const handleRowClick = (record) => {
    setSelectedOrgId(record._id);
    setShowDetailView(true);
  };

  // Handle back from detail view
  const handleBackFromDetail = () => {
    setShowDetailView(false);
    setSelectedOrgId(null);
  };

  // Toggle organization status
  const toggleStatus = async (orgId) => {
    try {
      const response = await organizationService.toggleStatus(orgId);
      if (response.success) {
        message.success(response.message);
        fetchOrganizations();
      }
    } catch (error) {
      message.error(error.message || "Failed to update organization status");
    }
  };

  // Delete organization
  const deleteOrganization = (orgId, orgName) => {
    confirm({
      title: "Delete Organization",
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to delete "${orgName}"? This action cannot be undone.`,
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const response = await organizationService.deleteOrganization(orgId);
          if (response.success) {
            message.success(response.message);
            fetchOrganizations();
          }
        } catch (error) {
          message.error(error.message || "Failed to delete organization");
        }
      },
    });
  };

  // Get plan color
  const getPlanColor = (plan) => {
    const colors = {
      free: "default",
      basic: "blue",
      premium: "gold",
      enterprise: "purple",
    };
    return colors[plan] || "default";
  };

  // Get status color
  const getStatusColor = (isActive) => {
    return isActive ? "success" : "error";
  };

  // Calculate summary statistics
  const summaryStats = {
    total: organizations.length,
    active: organizations.filter((org) => org.isActive).length,
    inactive: organizations.filter((org) => !org.isActive).length,
    totalUsers: organizations.reduce(
      (sum, org) => sum + (org.stats?.totalUsers || 0),
      0
    ),
  };

  // Table columns
  const columns = [
    {
      title: "Organization",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <Space>
          <Avatar
            icon={<BankOutlined />}
            style={{ backgroundColor: "#1890ff" }}
          />
          <div>
            <div style={{ fontWeight: 500 }}>{text}</div>
            <Text type="secondary" style={{ fontSize: "12px" }}>
              {record.subdomain}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: "Contact",
      dataIndex: ["contact", "email"],
      key: "contact",
      render: (email, record) => (
        <div>
          <div>{email}</div>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {record.contact?.phone || "No phone"}
          </Text>
        </div>
      ),
    },
    {
      title: "Plan",
      dataIndex: "plan",
      key: "plan",
      render: (plan) => (
        <Tag color={getPlanColor(plan)} style={{ textTransform: "capitalize" }}>
          {plan}
        </Tag>
      ),
    },
    {
      title: "Users",
      key: "users",
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <div>
            <UserOutlined style={{ marginRight: 4 }} />
            {record.stats?.totalUsers || 0} Total
          </div>
          <div>
            <TeamOutlined style={{ marginRight: 4, color: "#52c41a" }} />
            {record.stats?.activeUsers || 0} Active
          </div>
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "status",
      render: (isActive) => (
        <Badge
          status={isActive ? "success" : "error"}
          text={isActive ? "Active" : "Inactive"}
        />
      ),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => viewOrganization(record)}
            />
          </Tooltip>
          <Tooltip title={record.isActive ? "Deactivate" : "Activate"}>
            <Button
              type="text"
              icon={
                record.isActive ? (
                  <CloseCircleOutlined />
                ) : (
                  <CheckCircleOutlined />
                )
              }
              onClick={() => toggleStatus(record._id)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => deleteOrganization(record._id, record.name)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Show detail view if selected
  if (showDetailView && selectedOrgId) {
    return (
      <OrganizationDetail
        organizationId={selectedOrgId}
        onBack={handleBackFromDetail}
      />
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0 }}>
          Organizations Management
        </Title>
        <Text type="secondary">
          Manage all organizations registered in the system
        </Text>
      </div>

      {/* Summary Statistics */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Organizations"
              value={summaryStats.total}
              prefix={<BankOutlined />}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Active Organizations"
              value={summaryStats.active}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Inactive Organizations"
              value={summaryStats.inactive}
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: "#ff4d4f" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Users"
              value={summaryStats.totalUsers}
              prefix={<UserOutlined />}
              valueStyle={{ color: "#722ed1" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters and Search */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col xs={24} sm={12} md={8}>
            <Input
              placeholder="Search organizations..."
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              onPressEnter={handleSearch}
            />
          </Col>
          <Col xs={24} sm={6} md={4}>
            <Select
              style={{ width: "100%" }}
              placeholder="Plan"
              value={filters.plan}
              onChange={(value) => handleFilterChange("plan", value)}
            >
              <Option value="all">All Plans</Option>
              <Option value="free">Free</Option>
              <Option value="basic">Basic</Option>
              <Option value="premium">Premium</Option>
              <Option value="enterprise">Enterprise</Option>
            </Select>
          </Col>
          <Col xs={24} sm={6} md={4}>
            <Select
              style={{ width: "100%" }}
              placeholder="Status"
              value={filters.status}
              onChange={(value) => handleFilterChange("status", value)}
            >
              <Option value="all">All Status</Option>
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Space>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
              >
                Search
              </Button>
              <Button
                onClick={() => {
                  setFilters({ search: "", plan: "all", status: "all" });
                  fetchOrganizations({
                    search: "",
                    plan: undefined,
                    status: undefined,
                  });
                }}
              >
                Reset
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setCreateModalVisible(true)}
              >
                Create New Organization
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Organizations Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={organizations}
          rowKey="_id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} organizations`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
            style: { cursor: "pointer" },
          })}
        />
      </Card>

      {/* Organization Detail Modal */}
      <Modal
        title="Organization Details"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={800}
      >
        {selectedOrg && (
          <div>
            <Descriptions bordered column={2}>
              <Descriptions.Item label="Name" span={2}>
                {selectedOrg.name}
              </Descriptions.Item>
              <Descriptions.Item label="Subdomain">
                {selectedOrg.subdomain}
              </Descriptions.Item>
              <Descriptions.Item label="Plan">
                <Tag
                  color={getPlanColor(selectedOrg.plan)}
                  style={{ textTransform: "capitalize" }}
                >
                  {selectedOrg.plan}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Badge
                  status={selectedOrg.isActive ? "success" : "error"}
                  text={selectedOrg.isActive ? "Active" : "Inactive"}
                />
              </Descriptions.Item>
              <Descriptions.Item label="Created">
                {new Date(selectedOrg.createdAt).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {selectedOrg.contact?.email}
              </Descriptions.Item>
              <Descriptions.Item label="Phone">
                {selectedOrg.contact?.phone || "Not provided"}
              </Descriptions.Item>
              <Descriptions.Item label="Total Users">
                {selectedOrg.stats?.totalUsers || 0}
              </Descriptions.Item>
              <Descriptions.Item label="Active Users">
                {selectedOrg.stats?.activeUsers || 0}
              </Descriptions.Item>
              <Descriptions.Item label="Admin Users">
                {selectedOrg.stats?.adminUsers || 0}
              </Descriptions.Item>
              <Descriptions.Item label="Total Leads">
                {selectedOrg.stats?.totalLeads || 0}
              </Descriptions.Item>
              <Descriptions.Item label="Total Deals">
                {selectedOrg.stats?.totalDeals || 0}
              </Descriptions.Item>
              <Descriptions.Item label="Max Users">
                {selectedOrg.settings?.maxUsers || "Unlimited"}
              </Descriptions.Item>
              <Descriptions.Item label="Max Leads">
                {selectedOrg.settings?.maxLeads || "Unlimited"}
              </Descriptions.Item>
              <Descriptions.Item label="Features" span={2}>
                <Space wrap>
                  {selectedOrg.settings?.features?.map((feature) => (
                    <Tag key={feature} color="blue">
                      {feature.replace("_", " ").toUpperCase()}
                    </Tag>
                  ))}
                </Space>
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Modal>

      {/* Create New Organization Modal */}
      <Modal
        title="Create New Organization"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          createForm.resetFields();
        }}
        footer={[
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={() => createForm.submit()}
          >
            Create
          </Button>,
        ]}
        confirmLoading={loading}
      >
        <Form
          form={createForm}
          layout="vertical"
          name="create_organization_form"
          onFinish={async (values) => {
            setLoading(true);
            try {
              const response = await organizationService.createOrganization({
                name: values.name,
                subdomain: values.subdomain,
                contact: {
                  email: values.email,
                  first_name: values.first_name,
                  last_name: values.last_name,
                },
                plan: values.plan || "free", // Default to free plan
                password: values.password, // Pass the password field
              });

              if (response.success) {
                message.success(response.message);
                setCreateModalVisible(false);
                createForm.resetFields();
                fetchOrganizations(); // Refresh the list
              } else {
                message.error(
                  response.message || "Failed to create organization"
                );
              }
            } catch (error) {
              message.error(error.message || "Failed to create organization");
            } finally {
              setLoading(false);
            }
          }}
        >
          <Form.Item
            name="name"
            label="Organization Name"
            rules={[
              { required: true, message: "Please enter organization name" },
            ]}
          >
            <Input placeholder="e.g., Acme Corp" />
          </Form.Item>
          <Form.Item
            name="subdomain"
            label="Subdomain"
            rules={[
              { required: true, message: "Please enter subdomain" },
              {
                pattern: /^[a-z0-9-]+$/,
                message:
                  "Subdomain can only contain lowercase letters, numbers, and hyphens",
              },
            ]}
          >
            <Input
              addonBefore="https://"
              addonAfter=".crmpro.com"
              placeholder="your-company"
            />
          </Form.Item>
          <Form.Item noStyle dependencies={["subdomain"]}>
            {({ getFieldValue }) => {
              const subdomainValue = getFieldValue("subdomain");
              return (
                <div style={{ marginTop: "-8px" }}>
                  {" "}
                  {/* Adjust this value as needed */}
                  {subdomainValue ? (
                    <Text
                      type="secondary"
                      style={{
                        fontSize: "12px",
                        display: "block",
                      }}
                    >
                      This will be your unique CRM URL:{" "}
                      <a
                        href={`https://${subdomainValue}.crmpro.com`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        https://{subdomainValue}.crmpro.com
                      </a>
                    </Text>
                  ) : (
                    <Text
                      type="secondary"
                      style={{
                        fontSize: "12px",
                        display: "block",
                      }}
                    >
                      This will be your unique CRM URL:
                      https://your-subdomain.crmpro.com
                    </Text>
                  )}
                </div>
              );
            }}
          </Form.Item>
          <Form.Item
            name="email"
            label="Contact Email"
            rules={[
              { required: true, message: "Please enter contact email" },
              { type: "email", message: "Please enter a valid email" },
            ]}
          >
            <Input placeholder="e.g., admin@acmecorp.com" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="first_name"
                label="Admin First Name"
                rules={[
                  { required: true, message: "Please enter admin first name" },
                ]}
              >
                <Input placeholder="e.g., John" autoComplete="given-name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="last_name"
                label="Admin Last Name"
                rules={[
                  { required: true, message: "Please enter admin last name" },
                ]}
              >
                <Input placeholder="e.g., Doe" autoComplete="family-name" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: "Please enter a password" },
              { min: 8, message: "Password must be at least 8 characters" },
            ]}
            hasFeedback
          >
            <Input.Password placeholder="Enter password" autoComplete="new-password" />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Confirm Password"
            dependencies={["password"]}
            hasFeedback
            rules={[
              { required: true, message: "Please confirm your password" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error("The two passwords that you entered do not match!")
                  );
                },
              }),
            ]}
          >
            <Input.Password placeholder="Confirm password" autoComplete="new-password" />
          </Form.Item>

          <Form.Item name="plan" label="Subscription Plan" initialValue="free">
            <Select>
              <Option value="free">Free</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Organizations;
